import { motion, useReducedMotion } from 'framer-motion';
import { Loader2, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { extractErrorMessage } from '@/services/errors/errors';
import { WorkflowCard } from './WorkflowCard';

// Decaying stagger: first cards get more spacing, later ones cascade in faster.
// delay(i) = base * (1 - e^(-decay * i)) — asymptotically approaches `base`.
const staggerDelay = (i: number) => 0.32 * (1 - Math.exp(-0.9 * i));

const cardVariants = {
  hidden: { opacity: 0, y: 6 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: staggerDelay(i),
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1] // ease-out-quint
    }
  })
};

const reducedMotionCardVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: 0 }
  }
};

export type WorkflowsSectionProps = {
  processWorkflows: { id: string; name: string | null }[];
  publishedIds: Record<string, string>;
  isPrivileged: boolean;
  onSavePublishedId: (
    workflowId: string,
    publishedId: string | null
  ) => Promise<void>;
  onPreAllocate?: () => Promise<void>;
};

export const WorkflowsSection = ({
  processWorkflows,
  publishedIds,
  isPrivileged,
  onSavePublishedId,
  onPreAllocate
}: WorkflowsSectionProps) => {
  const shouldReduceMotion = useReducedMotion();
  const variants = shouldReduceMotion
    ? reducedMotionCardVariants
    : cardVariants;
  const [isAllocating, setIsAllocating] = useState(false);

  const hasMissingIds = processWorkflows.some(wf => !publishedIds[wf.id]);

  const handlePreAllocate = async () => {
    if (!onPreAllocate) {
      return;
    }
    setIsAllocating(true);
    try {
      await onPreAllocate();
      toast.success('Workflow IDs pre-allocated');
    } catch (error) {
      const message = extractErrorMessage(error);
      toast.error(`Pre-allocation failed: ${message}`);
    } finally {
      setIsAllocating(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-balance font-semibold text-lg">Workflows</h2>
        {isPrivileged && onPreAllocate && hasMissingIds && (
          <Button
            disabled={isAllocating}
            onClick={handlePreAllocate}
            size="sm"
            variant="outline"
          >
            {isAllocating ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="mr-2 h-3.5 w-3.5" />
            )}
            Pre-allocate IDs
          </Button>
        )}
      </div>
      {processWorkflows.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {processWorkflows.map((wf, i) => (
            <motion.div
              animate="show"
              custom={i}
              initial="hidden"
              key={wf.id}
              style={{ zIndex: processWorkflows.length - i }}
              variants={variants}
            >
              <WorkflowCard
                isPrivileged={isPrivileged}
                onSavePublishedId={onSavePublishedId}
                publishedId={publishedIds[wf.id] || null}
                workflowId={wf.id}
                workflowName={wf.name ?? 'Unknown workflow'}
              />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl bg-card px-5 py-8 text-center shadow-verail">
          <p className="text-pretty text-muted-foreground text-sm">
            No workflows found for this process.
          </p>
        </div>
      )}
    </div>
  );
};
